import {CurrencyPipe} from '@angular/common';
import {Component, Inject, Input, LOCALE_ID} from '@angular/core';
import {FormBuilder, ReactiveFormsModule} from '@angular/forms';
import {EmTranslatePipe, ToastService, TranslationService} from '@emma-labs/shared/angular';
import {
  getDisabilityRiderDurations,
  getDisabilityRiderMaxCoverage,
  HUMANIA_EMMA_V2_DISABILITY_RIDER_COVERAGE_AMOUNT_INTERVAL,
  HUMANIA_EMMA_V2_DISABILITY_RIDER_MAX_AGE,
  HUMANIA_EMMA_V2_DISABILITY_RIDER_MIN_COVERAGE,
  JobRiskClass,
  ProviderId,
  RiderType,
} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CardModule} from 'primeng/card';
import {DropdownModule} from 'primeng/dropdown';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {TableModule, TableRowSelectEvent} from 'primeng/table';
import {finalize} from 'rxjs';
import {ProtectionService} from '../../../../../services/protection.service';
import {CoverageCardComponent} from '../../coverage-card/coverage-card.component';
import {BaseRiderCardComponent} from '../base-rider-card.component';

const DEFAULT_SELECTED_DURATION = 2;

@UntilDestroy()
@Component({
  selector: 'em-disability-rider-card',
  templateUrl: './disability-rider-card.component.html',
  imports: [
    CardModule,
    CoverageCardComponent,
    DropdownModule,
    ReactiveFormsModule,
    CurrencyPipe,
    ProgressSpinnerModule,
    TableModule,
    EmTranslatePipe,
  ],
})
export class DisabilityRiderCardComponent extends BaseRiderCardComponent {
  @Input({required: true}) set jobRiskClass(_jobRiskClass: JobRiskClass | undefined) {
    // Temporary warning message until we refactor to stop resetting the coverage amount when not necessary
    if (this._jobRiskClass && this.form.controls.coverageAmount.value) {
      this.toastService.warn(
        this.translate.currentLanguageIsFr()
          ? `L'assurance invalidité a été réinitialisée.`
          : `Disability insurance has been reset.`,
      );
      this.form.patchValue({coverageAmount: undefined, duration: undefined});
    }

    this._jobRiskClass = _jobRiskClass;
    this.getCoverageAmountOptions();
    this.setProducts();
  }

  @Input({required: true}) override set selectedLifeInsuranceCoverageAmount(
    coverageAmount: number | undefined,
  ) {
    super.selectedLifeInsuranceCoverageAmount = coverageAmount;
    this.getCoverageAmountOptions();
  }

  @Input({required: true}) override set age(_age: number) {
    super.age = _age;
    if (_age > HUMANIA_EMMA_V2_DISABILITY_RIDER_MAX_AGE) {
      this.form.patchValue({coverageAmount: undefined, duration: undefined});
    }
    this.getCoverageAmountOptions();
  }

  readonly MAX_AGE = HUMANIA_EMMA_V2_DISABILITY_RIDER_MAX_AGE;
  _jobRiskClass: JobRiskClass | undefined;

  constructor(
    override readonly fb: FormBuilder,
    private readonly toastService: ToastService,
    private readonly translate: TranslationService,
    private readonly protectionService: ProtectionService,
    @Inject(LOCALE_ID) locale: string,
  ) {
    super(fb, locale);

    this.form.controls.coverageAmount.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.coverageSelected.emit({
        amount: value ?? undefined,
        duration: this.form.controls.duration.value ?? DEFAULT_SELECTED_DURATION,
        type: RiderType.DISABILITY,
      });
    });
  }

  setProducts(): void {
    if (
      !this._ratesVersion ||
      !this.form.controls.coverageAmount.value ||
      !this.form.controls.duration.value ||
      this._smoker == null ||
      this._age == null ||
      this._age > this.MAX_AGE ||
      !this._sex ||
      !this._jobRiskClass ||
      this._jobRiskClass === JobRiskClass.INELIGIBLE
    ) {
      this.products = [];
      return;
    }

    const riderConfigs = getDisabilityRiderDurations(this._jobRiskClass).map((duration) => ({
      type: RiderType.DISABILITY,
      duration,
      coverage: this.form.controls.coverageAmount.value!,
    }));

    this.protectionService
      .getRiders(this._ratesVersion, ProviderId.HUMANIA_EMMA_V2, riderConfigs, {
        age: this._age,
        smoker: this._smoker,
        sex: this._sex,
        jobRiskClass: this._jobRiskClass,
      })
      .pipe(finalize(() => (this.isPriceLoading = false)))
      .subscribe({
        next: (riders) => {
          this.selectedProduct = {name: {fr: 'Aucun', en: 'None'}, value: '0'};

          this.products = riders.map((rider, index) => {
            const product = {
              name: {
                en: `${rider.duration}-year`,
                fr: `${rider.duration} ans`,
              },
              value: rider.duration!.toString(),
              price: rider.price,
            };

            if (this.form.controls.duration.value === rider.duration) {
              this.selectedProduct = product;
            }

            return product;
          });
        },
        error: (_) => {
          this.toastService.error(
            this.translate.currentLanguageIsFr()
              ? `Une erreur est survenue lors du chargement de l'assurance invalidité. Veuillez réessayer`
              : `An error occurred while loading the disability rider. Please try again`,
          );
        },
      });
  }

  onSelect($event: TableRowSelectEvent): void {
    this.coverageSelected.emit({
      amount: this.form.controls.coverageAmount.value ?? undefined,
      duration: +$event.data.value,
      type: RiderType.DISABILITY,
    });
  }

  private getCoverageAmountOptions(): void {
    if (
      !this._selectedLifeInsuranceCoverageAmount ||
      !this._jobRiskClass ||
      (this._age && this._age > HUMANIA_EMMA_V2_DISABILITY_RIDER_MAX_AGE) ||
      this._jobRiskClass === JobRiskClass.INELIGIBLE
    ) {
      this.coverageAmountOptions = [];
      return;
    }

    const maxCoverage = getDisabilityRiderMaxCoverage(
      this._selectedLifeInsuranceCoverageAmount,
      this._jobRiskClass,
    );
    const canSelectNone = maxCoverage >= HUMANIA_EMMA_V2_DISABILITY_RIDER_MIN_COVERAGE;
    this.coverageAmountOptions = this.protectionService.getCoverageDropdownOptions(
      HUMANIA_EMMA_V2_DISABILITY_RIDER_MIN_COVERAGE,
      HUMANIA_EMMA_V2_DISABILITY_RIDER_COVERAGE_AMOUNT_INTERVAL,
      maxCoverage,
      canSelectNone,
    );
  }
}
